import React, { useState, useEffect } from 'react';
import { Bot, AlertCircle, Loader2 } from 'lucide-react';

/**
 * Professional Login Modal Component
 * Features:
 * - Modal popup design
 * - Company branding support
 * - Microsoft/Azure AD authentication only
 * - Error handling
 * - Loading states
 */
const LoginPage = ({
  onLogin,
  loginWithAzure,
  loading = false,
  authError = null,
  companyLogo,
  companyName = "Maintenance AI",
  isOpen = true,
  onClose = null
}) => {
  const [error, setError] = useState('');

  const handleMicrosoftLogin = async () => {
    setError('');
    if (loginWithAzure) {
      const result = await loginWithAzure();
      if (result && result.success) {
        if (onLogin) {
          onLogin(result.user);
        }
      } else if (result) {
        setError(result.error || 'Failed to sign in with Microsoft');
      }
      // If result is undefined, the page is redirecting to Microsoft
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity"
        onClick={onClose ? onClose : undefined}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all">
          <div className="p-8">
            {/* Logo and Title */}
            <div className="text-center mb-8">
              {companyLogo ? (
                <img src={companyLogo} alt="Company Logo" className="h-16 mx-auto mb-4" />
              ) : (
                <div className="h-16 w-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Bot className="h-8 w-8 text-white" />
                </div>
              )}
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {companyName}
              </h1>
              <p className="text-gray-600">
                Enterprise Maintenance Analytics Platform
              </p>
            </div>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
              Sign in to continue
            </h2>

            {/* Error Message */}
            {(error || authError) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-red-700 font-medium">Error</p>
                  <p className="text-sm text-red-600 mt-1">{error || authError}</p>
                </div>
              </div>
            )}

            {/* Loading Indicator */}
            {loading && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
                <Loader2 className="h-5 w-5 text-blue-600 mr-3 animate-spin" />
                <p className="text-sm text-blue-700">Signing in...</p>
              </div>
            )}

            {/* Microsoft Login Button */}
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={loading}
              className="w-full flex justify-center items-center py-4 px-4 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                  Redirecting to Microsoft...
                </>
              ) : (
                <>
                  <svg className="h-6 w-6 mr-3" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.5 0L0 0L0 10.5L10.5 10.5L10.5 0Z" fill="#F25022" />
                    <path d="M21 0L10.5 0L10.5 10.5L21 10.5L21 0Z" fill="#7FBA00" />
                    <path d="M10.5 10.5L0 10.5L0 21L10.5 21L10.5 10.5Z" fill="#00A4EF" />
                    <path d="M21 10.5L10.5 10.5L10.5 21L21 21L21 10.5Z" fill="#FFB900" />
                  </svg>
                  Sign in with Microsoft
                </>
              )}
            </button>

            {/* Footer */}
            <div className="mt-8 text-center">
              <p className="text-xs text-gray-500">
                Protected by enterprise security standards
              </p>
            </div>

            {/* Additional Info */}
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">
                Need help?{' '}
                <a
                  href="mailto:zhiwei.chen@bravus.com.au?subject=Login Support Request"
                  className="text-blue-600 hover:text-blue-700 font-medium underline cursor-pointer"
                >
                  Contact support
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
